import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { IZoomDAO, IZoomMeetingDTO, ZoomMeetingType } from '../../../../api/zoom.dao';
import { IRouter } from '../../../../infrastructure/router';
import { RouteParams, Routes } from '../../../../routes-table';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { MeetingFieldsViewModel } from '../shared/meeting-form-fields/meeting-form-fields.viewmodel';
import { alertServerError } from '../../../global/modal/alerts-host/alert-templates';

class ScheduleNewMeetingViewModel {
    @mobx.observable
    public loading: boolean = false;
    @mobx.observable
    private error: string;
    @mobx.observable
    private meeting: IZoomMeetingDTO;

    private formFields: MeetingFieldsViewModel;
    private meetingId: number;

    constructor(
        public clonedMeetingId: number,
        private readonly zoomDao: IZoomDAO = IoC.Container.get(IZoomDAO),
        private readonly router: IRouter<RouteParams> = IoC.Container.get(IRouter),
        private readonly errorParsing: IErrorParsing = IoC.Container.get(IErrorParsing),
    ) {
        this.formFields = new MeetingFieldsViewModel();
    }

    public getFormFields() {
        return this.formFields;
    }

    public getIsLoading = () => {
        return this.loading;
    };

    public getMeetingId = () => {
        return this.meetingId;
    };

    public getClonedMeetingId = () => {
        return this.clonedMeetingId;
    };


    @mobx.action
    public fetchMeetingDetails = async () => {
        this.loading = true;

        try {
            const clonedMeetingDetails = await this.zoomDao.getScheduledMeetingDetails(this.clonedMeetingId);
            mobx.runInAction(() => {
                this.formFields = new MeetingFieldsViewModel(clonedMeetingDetails);
            });
        } catch (e) {
            mobx.runInAction(() => {
                this.error = this.errorParsing.extractAPIError(e).errorMessage;
                throw e;
            });
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public cleanRecources = () => {
        this.formFields.startDateTime = null;
        this.formFields.endDateTime = null;
        this.formFields.timeZone = '';
        this.formFields.topic = '';
        this.formFields.description = '';
        this.formFields.hideErrorMessages();
        this.meetingId = null;
    };

    @mobx.action
    public createNewMeeting = async () => {
        this.meetingId = null;
        this.loading = true;

        const scheduleMeetingParams = {
            startTime: this.formFields.startDateTime,
            endTime: this.formFields.endDateTime,
            timeZone: this.formFields.timeZone,
            topic: this.formFields.topic,
            agenda: this.formFields.description,
            zoomGroupId: this.formFields.selectedGroupId || null,
            type: ZoomMeetingType.SCHEDULED_MEETING,
            accountId: null
        };

        try {
            this.meetingId = (await this.zoomDao.scheduleNewMeeting(scheduleMeetingParams)).id;
            mobx.runInAction(() => {
                if (this.meetingId) {
                    this.router.navigate(Routes.ZoomScheduledMeetingDetails, { meetingId: this.meetingId.toString() });
                }
            });
        } catch (e) {
            alertServerError('Error', this.errorParsing.extractMessage(e));
        } finally {
            mobx.runInAction(() => {
                this.loading = false;
            });
        }
    };

    @mobx.action
    public submitForm = async () => {
        if (!this.formFields.preSubmitValidation()) {
            return;
        }

        this.formFields.hideErrorMessages();
        await this.createNewMeeting();
    };
}

export { ScheduleNewMeetingViewModel };
